import React from 'react';

/* ============================================================
   内部研发办公室 · 共享类型/工具/小部件
   （只读镜像数字员工后台，本目录内所有设计共用）
   ============================================================ */

export interface DevEmployee {
  id: string;
  name: string;
  role: string;
  status: string;
  personality?: string;
}

export interface DevMember {
  id?: string;
  employeeId?: string;
  name?: string;
  role?: string;
  status?: string;
  displayTitle?: string;
  displaySummary?: string;
  displayWaiting?: string;
  current?: string;
}

export interface DevSummary {
  activeTasks?: number;
  pendingConfirmations?: number;
  runningDelegates?: number;
  blockedTasks?: number;
  displayStatus?: string;
  displayText?: string;
  members?: DevMember[];
}

export interface DevConvo {
  id: string;
  content?: string;
  fromEmployee?: { id?: string; name?: string; role?: string } | null;
  toEmployee?: { id?: string; name?: string; role?: string } | null;
  createdAt?: string;
}

export interface DevEvent {
  id: string;
  event?: string;
  message?: string;
  level?: string;
  createdAt?: string;
  employee?: { id?: string; name?: string; role?: string } | null;
}

export interface DevFileNode {
  name: string;
  type?: string;
  path?: string;
  children?: DevFileNode[];
}

/** 数据总线：外壳轮询后台，所有设计只读消费 */
export interface DevData {
  project: { id: string; name: string };
  employees: DevEmployee[];
  summary: DevSummary | null;
  convos: DevConvo[];      // 旧 → 新
  events: DevEvent[];      // 新 → 旧
  files: DevFileNode[];
  pending: number;         // 待用户确认数（本页只提醒，不可办理）
  loaded: boolean;
  /** 员工当前在忙什么（对不上则 null） */
  busyOf: (e: DevEmployee) => string | null;
}

/* ---------------- 状态与角色词典 ---------------- */

export const STATUS_META: Record<string, { label: string; tone: 'run' | 'idle' | 'warn' | 'bad' }> = {
  idle: { label: '空闲待命', tone: 'idle' },
  thinking: { label: '思考中', tone: 'run' },
  working: { label: '执行中', tone: 'run' },
  delegating: { label: '派活中', tone: 'run' },
  review: { label: '审阅中', tone: 'run' },
  walking: { label: '走动中', tone: 'idle' },
  waiting_user: { label: '等用户确认', tone: 'warn' },
  blocked: { label: '受阻', tone: 'bad' },
  error: { label: '异常', tone: 'bad' },
  interrupted: { label: '已暂停', tone: 'warn' },
  completed: { label: '刚完成', tone: 'idle' },
};

export const statusMeta = (s?: string) => STATUS_META[s || ''] || { label: s || '在岗', tone: 'idle' as const };

export const isBusyStatus = (s?: string) => statusMeta(s).tone === 'run';

const ROLE_COLORS: Array<[RegExp, string]> = [
  [/项目经理|\bPM\b/i, '#fbbf24'],
  [/架构/, '#38bdf8'],
  [/产品/, '#a78bfa'],
  [/UI|设计|前端/i, '#f472b6'],
  [/后端|开发|全栈|工程师/, '#34d399'],
  [/测试|质量|QA|验收/i, '#f87171'],
  [/运维|部署|交付/, '#2dd4bf'],
  [/文档|资料/, '#c4b5fd'],
];

export const roleColor = (role?: string) => {
  for (const [re, c] of ROLE_COLORS) if (re.test(role || '')) return c;
  return '#7ea2d8';
};

/** 角色缩写（徽章/印章/头像用） */
export const roleShort = (role?: string) => {
  const r = role || '';
  if (/项目经理|PM/i.test(r)) return 'PM';
  if (/架构/.test(r)) return '架构';
  if (/产品/.test(r)) return '产品';
  if (/前端/.test(r)) return '前端';
  if (/后端/.test(r)) return '后端';
  if (/测试|QA/i.test(r)) return '测试';
  if (/UI|设计/i.test(r)) return '设计';
  if (/运维/.test(r)) return '运维';
  return r.slice(0, 2) || '成员';
};

export const dvoTime = (ts?: string) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const clip = (s: string | undefined, n: number) => {
  const t = (s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

/** 机器人立绘（部分设计复用） */
export const DVO_BOTS = [
  '/aco-assets/bot-tech-idle.webp',
  '/aco-assets/bot-monitor-idle.webp',
  '/aco-assets/bot-design-idle.webp',
  '/aco-assets/bot-toll-idle.webp',
  '/aco-assets/bot-station-idle.webp',
  '/aco-assets/bot-audit-idle.webp',
];

/** 写实数字人立绘（卷宗等设计用） */
export const DVO_STAFF = [
  '/aco-assets/mgr-tech-v2.webp',
  '/aco-assets/staff-a-v2.webp',
  '/aco-assets/staff-b-v2.webp',
  '/aco-assets/staff-c-v2.webp',
  '/aco-assets/mgr-design-v2.webp',
  '/aco-assets/mgr-monitor-v2.webp',
];

/* ---------------- 共享小部件 ---------------- */

/** 五项态势数据条（各设计用 CSS 换肤） */
export const StatStrip: React.FC<{ D: DevData }> = ({ D }) => {
  const s = D.summary;
  return (
    <div className="dvo-stats">
      <span><b>{D.employees.length}</b><em>数字员工</em></span>
      <span><b>{s?.activeTasks ?? '—'}</b><em>进行中任务</em></span>
      <span><b>{s?.runningDelegates ?? '—'}</b><em>执行中委派</em></span>
      <span className={s?.blockedTasks ? 'bad' : ''}><b>{s?.blockedTasks ?? '—'}</b><em>受阻</em></span>
      <span className={D.pending ? 'warn' : ''}><b>{D.pending}</b><em>待您确认</em></span>
    </div>
  );
};

/** 沟通流水（只读，各设计换肤） */
export const FeedList: React.FC<{ D: DevData; limit?: number }> = ({ D, limit = 24 }) => {
  const list = D.convos.slice(-limit);
  return (
    <div className="dvo-feed">
      {list.length === 0 && <div className="dvo-empty">团队暂无沟通动态</div>}
      {list.map((c, i) => (
        <div key={c.id || i} className="row">
          <div className="who">
            <b style={{ color: roleColor(c.fromEmployee?.role) }}>{c.fromEmployee?.name || '系统'}</b>
            {c.toEmployee?.name && <i>→ {c.toEmployee.name}</i>}
            <u>{dvoTime(c.createdAt)}</u>
          </div>
          <p>{clip(c.content, 120)}</p>
        </div>
      ))}
    </div>
  );
};

/** 待确认提醒（铁律：本页只提醒，不可点开办理） */
export const ConfirmHint: React.FC<{ D: DevData }> = ({ D }) => {
  if (!D.pending) return null;
  return (
    <div className="dvo-confirm-hint">
      <i className="bell">🔔</i>
      <b>{D.pending} 个问题需要您确认</b>
      <span>请返回 AI 指挥办公室，在黄色提醒卡中填写意见并点击「确认反馈」</span>
    </div>
  );
};

/** 员工当前工作一句话 */
export const workLine = (D: DevData, e: DevEmployee) => {
  const busy = D.busyOf(e);
  if (busy) return busy;
  return statusMeta(e.status).label;
};

/** 团队名单（employees 未回来时用 summary.members 兜底） */
export const teamOf = (D: DevData): DevEmployee[] => {
  if (D.employees.length) return D.employees;
  return (D.summary?.members || []).map((m, i) => ({
    id: m.employeeId || m.id || `m-${i}`,
    name: m.name || `成员${i + 1}`,
    role: m.role || '数字员工',
    status: m.status || 'idle',
  }));
};
